import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import './ProgressBar.scss';
import { Body } from '@components/text';

export interface ProgressBarProps {
  className?: string;
  /** optional meta text to right of progress bar */
  label?: string;
  /** integer (0-100) percent full. default 0 */
  percent?: number;
  /** small or large size. default large */
  size?: 'small' | 'large';
  /** if true apply rounded edges style. default false */
  isRound?: boolean;
  /** if true apply progress animation on load */
  hasAnimation?: boolean;
  /** color scheme. default primary */
  colorScheme?: 'primary' | 'brand';
}

/**
 * ProgressBar component
 * @param className - optional class
 * @param label - optional meta text to right of progress bar
 * @param percent - integer (0-100) percent full. default 0
 * @param size - small or large size. default large
 * @param isRound - if true apply rounded edges style. default false
 * @param hasAnimation - if true apply progress animation on load
 * @param colorScheme - color scheme. default primary
 */

export const ProgressBar: React.FC<ProgressBarProps> = ({
  className,
  label,
  percent = 0,
  size = 'large',
  isRound,
  hasAnimation,
  colorScheme = 'primary',
  ...rest
}) => {
  const labelTextSize = size === 'large' ? 'medium' : 'small';

  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${percent}%`;
    }
  }, [percent]);

  return (
    <div
      className={classNames('vds-progress-bar', `vds-progress-bar--${colorScheme}`, className)}
      {...rest}>
      <div
        className={classNames(
          'vds-progress-bar__wrapper',
          `vds-progress-bar__wrapper--${size}`,
          isRound && 'vds-progress-bar__wrapper-rounded',
        )}>
        <div
          ref={progressBarRef}
          className={classNames(
            'vds-progress-bar__inner',
            hasAnimation && 'vds-progress-bar__inner-animated',
          )}
          style={{ width: hasAnimation ? `0` : `${percent}%` }}
        />
      </div>
      {label && (
        <Body variant="metadata" weight="semibold" size={labelTextSize}>
          {label}
        </Body>
      )}
    </div>
  );
};
